import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronRight, Compass, Info, Loader2, Map, Star } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { getChallengeStampUrl } from "@/lib/challengeStamps";
import TakeTourModal from "@/components/app/TakeTourModal";

// Camper home screen — 1:1 with the mobile app's Home tab.
//
//   • time-of-day greeting + Premium pill + "Ready for your next adventure?"
//   • Take a Tour helper strip
//   • Large forest-green "My Passport" card with the profile avatar,
//     Stamps / Campsites / States stats, orange EXP bar (label uses "EXP",
//     not "XP") and a small orange star badge on the avatar for premium.
//   • Featured orange "SEASONAL CHALLENGE" strip (Merdeka Camper 2026 &
//     any future seasonal challenge — sourced from /challenges/status
//     `special[]`).
//   • Recent Check-ins section with a wood/beige "No check-ins yet"
//     empty state and a forest-green "Explore Campsites" CTA.
export default function Passport() {
  const { user, api } = useAuth();
  const [checkins, setCheckins] = useState(null);
  const [challenges, setChallenges] = useState(null);
  const [error, setError] = useState(null);
  const [tourOpen, setTourOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.get("/checkins/mine", { params: { limit: 12, paginated: true, status: "validated" } })
      .then(({ data }) => { if (!cancelled) setCheckins(data?.items || []); })
      .catch((e) => { if (!cancelled) setError(e?.response?.data?.detail || "Could not load your stamps."); });
    return () => { cancelled = true; };
  }, [api]);

  // Featured "SEASONAL" challenge — we take the first entry in the
  // `special` bucket returned by /challenges/status. That's the same
  // bucket the mobile app uses for Merdeka Camper 2026 and any future
  // country / seasonal challenges.
  useEffect(() => {
    let cancelled = false;
    api.get("/challenges/status")
      .then(({ data }) => { if (!cancelled) setChallenges(data); })
      .catch(() => { /* silent — a seasonal card is best-effort */ });
    return () => { cancelled = true; };
  }, [api]);

  // Auto-open the Take-a-Tour modal on a camper's very first landing.
  // Dismissal is remembered per-user in localStorage so returning users
  // aren't nagged; the "Take a Tour" banner remains the manual entry
  // point for anyone who wants to re-watch it.
  useEffect(() => {
    if (!user?.user_id) return;
    try {
      if (!localStorage.getItem(`mp_tour_dismissed:${user.user_id}`)) {
        setTourOpen(true);
      }
    } catch { /* localStorage disabled — silently skip auto-open */ }
  }, [user?.user_id]);

  const closeTour = () => {
    setTourOpen(false);
    if (user?.user_id) {
      try { localStorage.setItem(`mp_tour_dismissed:${user.user_id}`, "1"); } catch { /* ignore */ }
    }
  };

  const featuredSeasonal = useMemo(() => {
    const special = challenges?.special || [];
    // Prefer the first not-yet-claimed special challenge; fall back to
    // whichever is first so an "already claimed" seasonal still shows
    // when the user re-visits the tab.
    return special.find((c) => !c.claimed) || special[0] || null;
  }, [challenges]);

  const displayName = user?.display_name || user?.name || "Camper";
  const firstName = displayName.split(" ")[0] || displayName;
  const greeting = timeOfDayGreeting();

  const nextXp = user?.next_level_xp;
  const levelMin = user?.level_min_xp ?? 0;
  const xp = user?.xp ?? 0;
  const progressPct = useMemo(() => {
    if (!nextXp) return 100;
    const span = Math.max(1, nextXp - levelMin);
    return Math.min(100, Math.round(((xp - levelMin) / span) * 100));
  }, [xp, levelMin, nextXp]);

  // Stats — Campsites & States are derived from the loaded validated
  // check-ins (checkin.campsite_id, checkin.campsite_state). This keeps
  // parity with the mobile app which shows the same three numbers.
  const stamps = checkins?.length ?? 0;
  const distinctCampsites = useMemo(() => {
    if (!checkins) return 0;
    return new Set(checkins.map((c) => c.campsite_id).filter(Boolean)).size;
  }, [checkins]);
  const distinctStates = useMemo(() => {
    if (!checkins) return 0;
    return new Set(checkins.map((c) => c.campsite_state).filter(Boolean)).size;
  }, [checkins]);

  const avatarSrc = user?.avatar_camper || user?.avatar_data || user?.avatar || null;

  return (
    <div className="flex flex-col gap-5 md:gap-6 pb-4" data-testid="passport-page">
      {/* Greeting */}
      <header className="pt-2">
        <div className="text-sm" style={{ color: "var(--on-surface-muted)" }}>
          {greeting},
        </div>
        <div className="flex flex-wrap items-center gap-3 mt-0.5">
          <h1 className="text-3xl md:text-4xl font-extrabold" style={{ color: "var(--on-surface)" }}
              data-testid="passport-greeting-name">
            {firstName} <span aria-hidden>👋</span>
          </h1>
          {user?.is_premium && (
            <span
              className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-extrabold"
              style={{ background: "var(--premium)", color: "white" }}
              data-testid="passport-premium-pill"
            >
              <Star className="w-3.5 h-3.5" fill="currentColor" /> Premium
            </span>
          )}
        </div>
        <p className="mt-2 text-sm" style={{ color: "var(--on-surface-muted)" }}>
          Ready for your next adventure?
        </p>
      </header>

      {/* Take a Tour */}
      <button
        type="button"
        onClick={() => setTourOpen(true)}
        className="rounded-2xl p-4 flex items-center gap-3 border transition hover:-translate-y-0.5 text-left w-full"
        style={{ background: "var(--card)", borderColor: "var(--border-c)" }}
        data-testid="passport-take-a-tour"
      >
        <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0"
             style={{ background: "color-mix(in srgb, var(--brand) 10%, transparent)", color: "var(--brand)" }}>
          <Compass className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-extrabold text-base leading-tight" style={{ color: "var(--on-surface)" }}>
            Take a Tour
          </div>
          <div className="text-xs mt-0.5" style={{ color: "var(--on-surface-muted)" }}>
            New here? See how MyAdventure-Passport works in 3 quick steps.
          </div>
        </div>
        <ChevronRight className="w-5 h-5 opacity-50" style={{ color: "var(--on-surface-muted)" }} />
      </button>

      {/* My Passport + Seasonal challenge — stack on mobile, 2-col on
          desktop. My Passport takes the wider (3/5) column since it's
          the primary card. */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-5 md:gap-6 md:items-stretch">
        {/* My Passport — big forest-green card */}
        <Link
          to="/passport/stamps"
          className={`rounded-2xl p-5 md:p-7 relative overflow-hidden block h-full ${featuredSeasonal ? "md:col-span-3" : "md:col-span-5"}`}
          style={{ background: "var(--brand-deep)", color: "white" }}
          data-testid="my-passport-card"
        >
          <div className="flex gap-4 h-full">
            <div className="flex-1 min-w-0 pr-4 flex flex-col">
              <h2 className="text-2xl md:text-3xl font-extrabold">My Passport</h2>
              <p className="text-sm md:text-base mt-0.5" style={{ color: "rgba(255,255,255,0.72)" }}>
                View your stamps &amp; progress
              </p>

              {/* stats */}
              <div className="mt-5 grid grid-cols-3 gap-3 md:gap-5">
                <PassportStat testId="passport-stat-stamps" value={stamps} label="Stamps" />
                <PassportStat testId="passport-stat-campsites" value={distinctCampsites} label="Campsites" />
                <PassportStat testId="passport-stat-states" value={distinctStates} label="States" />
              </div>

              {/* level + EXP */}
              <div className="mt-5 md:mt-auto md:pt-5">
                <div className="flex items-center gap-1.5 text-sm font-extrabold" style={{ color: "var(--premium-muted)" }}>
                  Level {user?.level ?? 1} · {user?.title || "Newbie Camper"}
                  <Info className="w-3.5 h-3.5 opacity-70" />
                </div>
                <div className="mt-2 w-full h-2 rounded-full overflow-hidden"
                     style={{ background: "rgba(255,255,255,0.15)" }}>
                  <div
                    className="h-full rounded-full transition-all"
                    style={{ width: `${progressPct}%`, background: "var(--premium)" }}
                    data-testid="passport-progress-bar"
                  />
                </div>
                <div className="mt-1.5 text-[11px] font-bold" style={{ color: "rgba(255,255,255,0.75)" }}>
                  {user?.is_max_level
                    ? "Max level"
                    : `${xp.toLocaleString()} / ${(nextXp ?? xp).toLocaleString()} EXP`}
                </div>
              </div>
            </div>

            {/* avatar w/ premium star badge */}
            <div className="relative shrink-0 self-start mt-1">
              {avatarSrc ? (
                <img
                  src={avatarSrc}
                  alt=""
                  className="w-20 h-20 md:w-24 md:h-24 rounded-full object-cover"
                  style={{ boxShadow: "0 0 0 3px rgba(255,255,255,0.85)" }}
                />
              ) : (
                <div
                  className="w-20 h-20 md:w-24 md:h-24 rounded-full flex items-center justify-center text-2xl md:text-3xl font-extrabold"
                  style={{ background: "var(--premium)", color: "white",
                           boxShadow: "0 0 0 3px rgba(255,255,255,0.85)" }}
                >
                  {(firstName[0] || "M").toUpperCase()}
                </div>
              )}
              {user?.is_premium && (
                <span
                  className="absolute -top-1 -right-1 w-7 h-7 rounded-full flex items-center justify-center"
                  style={{ background: "var(--premium)", boxShadow: "0 0 0 2px white" }}
                  aria-hidden
                >
                  <Star className="w-3.5 h-3.5" style={{ color: "white" }} fill="currentColor" />
                </span>
              )}
            </div>
          </div>
        </Link>

        {/* Seasonal challenge */}
        {featuredSeasonal && (
          <div className="md:col-span-2 h-full">
            <SeasonalChallengeCard ch={featuredSeasonal} />
          </div>
        )}
      </div>

      {/* Recent check-ins */}
      <section className="mt-1">
        <div className="flex items-end justify-between mb-3">
          <h2 className="text-xl font-extrabold" style={{ color: "var(--on-surface)" }}>
            Recent Check-ins
          </h2>
          <Link
            to="/passport/stamps"
            className="text-sm font-extrabold"
            style={{ color: "var(--brand)" }}
            data-testid="recent-checkins-see-all"
          >
            See All
          </Link>
        </div>

        {checkins == null && (
          <div className="rounded-2xl p-6 flex items-center gap-3 border"
               style={{ background: "var(--card)", borderColor: "var(--border-c)", color: "var(--on-surface-muted)" }}>
            <Loader2 className="w-4 h-4 animate-spin" /> Loading your check-ins…
          </div>
        )}

        {checkins && checkins.length === 0 && (
          <div
            className="rounded-2xl p-8 text-center flex flex-col items-center gap-3"
            style={{ background: "var(--surface-secondary)" }}
            data-testid="recent-checkins-empty"
          >
            <Map className="w-9 h-9" style={{ color: "var(--on-surface-muted)" }} />
            <div className="text-lg font-extrabold" style={{ color: "var(--on-surface)" }}>
              No check-ins yet
            </div>
            <div className="text-sm" style={{ color: "var(--on-surface-muted)" }}>
              Explore campsites to earn your first stamp
            </div>
            <Link
              to="/explore"
              className="mt-2 inline-flex items-center justify-center px-5 py-2.5 rounded-full text-sm font-extrabold"
              style={{ background: "var(--brand-deep)", color: "white" }}
              data-testid="recent-checkins-explore-btn"
            >
              Explore Campsites
            </Link>
          </div>
        )}

        {checkins && checkins.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
            {checkins.slice(0, 6).map((c) => (
              <CheckinRow key={c.checkin_id} c={c} />
            ))}
          </div>
        )}

        {error && (
          <div className="rounded-2xl p-4 mt-3 text-sm border"
               style={{ background: "var(--card)", borderColor: "var(--border-c)", color: "var(--premium)" }}>
            {error}
          </div>
        )}
      </section>

      <TakeTourModal
        open={tourOpen}
        onClose={closeTour}
        firstName={firstName}
      />
    </div>
  );
}

function timeOfDayGreeting() {
  const h = new Date().getHours();
  if (h < 12) return "Good Morning";
  if (h < 17) return "Good Afternoon";
  return "Good Evening";
}

function PassportStat({ value, label, testId }) {
  return (
    <div data-testid={testId}>
      <div className="text-2xl md:text-3xl font-extrabold leading-none">
        {Number(value).toLocaleString()}
      </div>
      <div className="text-[11px] mt-1" style={{ color: "rgba(255,255,255,0.72)" }}>
        {label}
      </div>
    </div>
  );
}

function SeasonalChallengeCard({ ch }) {
  const progress = ch.progress ?? 0;
  const target = ch.target ?? 0;
  const reward = ch.reward ?? 0;
  // Prefer a matched stamp; fall back to the Merdeka artwork so the
  // seasonal card is never left with the generic app logo during the
  // Merdeka Camper 2026 campaign.
  const icon = getChallengeStampUrl(ch) || "/assets/stamps/merdeka-2026.webp";
  const title = ch.name || "Merdeka Camper 2026";

  return (
    <Link
      to="/challenges"
      className="rounded-2xl p-4 md:p-6 flex md:flex-col items-center md:items-start gap-4 md:gap-4 relative overflow-hidden block h-full"
      style={{
        background:
          "linear-gradient(135deg, #e07a1f 0%, #d97706 100%)",
        color: "white",
      }}
      data-testid="seasonal-challenge-card"
    >
      <div className="w-16 h-16 md:w-24 md:h-24 shrink-0 flex items-center justify-center overflow-hidden">
        <img
          src={icon}
          alt=""
          className="w-full h-full object-contain"
          style={{ filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.18))" }}
        />
      </div>
      <div className="flex-1 min-w-0 md:w-full">
        <div className="text-[10px] md:text-[11px] font-extrabold uppercase tracking-widest"
             style={{ color: "rgba(255,255,255,0.85)" }}>
          Seasonal Challenge
        </div>
        {/* Featured title — highlighted, all-caps, slightly bigger with
            a subtle text-shadow so it reads clearly on the orange
            gradient. Uses the challenge name when the backend supplies
            one, falls back to "Merdeka Camper 2026" so the card never
            renders headless. */}
        <div
          className="mt-1 md:mt-1.5 font-extrabold uppercase tracking-wide leading-tight text-xl md:text-3xl truncate md:whitespace-normal md:overflow-visible md:text-clip"
          style={{
            color: "#fff8e6",
            textShadow: "0 1px 2px rgba(0,0,0,0.25)",
          }}
          data-testid="seasonal-challenge-title"
        >
          {title}
        </div>
        <div className="text-xs md:text-sm mt-1 md:mt-2" style={{ color: "rgba(255,255,255,0.92)" }}>
          Progress {progress}/{target}
          {reward > 0 && ` · +${reward} EXP`}
        </div>
        {target > 0 && (
          <div className="hidden md:block mt-3 w-full h-2 rounded-full overflow-hidden"
               style={{ background: "rgba(255,255,255,0.25)" }}>
            <div
              className="h-full rounded-full transition-all"
              style={{
                width: `${Math.min(100, Math.round((progress / target) * 100))}%`,
                background: "white",
              }}
            />
          </div>
        )}
      </div>
      <ChevronRight className="w-5 h-5 shrink-0 opacity-90 md:self-end" />
    </Link>
  );
}

function CheckinRow({ c }) {
  const stampImage = c.campsite_stamp_url || c.campsite_logo_data || c.campsite_photo;
  const date = (c.checkin_date || c.created_at || "").slice(0, 10);
  return (
    <Link
      to={c.campsite_id ? `/campsites/${c.campsite_id}` : "#"}
      className="rounded-2xl p-3 flex items-center gap-3 border transition hover:-translate-y-0.5"
      style={{ background: "var(--card)", borderColor: "var(--border-c)" }}
      data-testid={`recent-checkin-${c.checkin_id}`}
    >
      <div
        className="w-14 h-14 rounded-xl shrink-0 flex items-center justify-center overflow-hidden"
        style={{ background: "color-mix(in srgb, var(--brand) 10%, transparent)" }}
      >
        {stampImage ? (
          <img src={stampImage} alt="" className="w-full h-full object-cover" />
        ) : (
          <Map className="w-6 h-6" style={{ color: "var(--brand)" }} />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-extrabold truncate" style={{ color: "var(--on-surface)" }}>
          {c.campsite_name || "Unknown campsite"}
        </div>
        <div className="text-[11px] mt-0.5" style={{ color: "var(--on-surface-muted)" }}>
          {[c.campsite_state, date].filter(Boolean).join(" · ")}
        </div>
      </div>
      <ChevronRight className="w-4 h-4 opacity-40" />
    </Link>
  );
}
